import { FloatingFormItem, Group } from '@/components/_common';
import { useWorkOrderItems } from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.hooks';
import notificationError from '@/utils/functions/notificationError';
import { applyMask, removeMask } from '@/utils/mask';
import {
  Modal,
  Space,
  Typography,
  Form,
  Button,
  Alert,
  ModalProps,
  message,
  Popover,
  AutoComplete,
  Tooltip,
  Checkbox,
} from 'antd';
import React, { useCallback, useEffect } from 'react';
import { FaExternalLinkAlt } from 'react-icons/fa';
import { useWorkorderDetailContext } from '../../../WorkorderDetailProvider';
import { wppTags, wppTextFormat, wppDefaultWppText } from '../options';
import TextareaVars from '@/components/_common/TextareaVars';
import { useCustomerContacts } from '@/core/domain/people/CustomerContacts/CustomerContacts.hooks';
import flatContacts from '@/utils/functions/flatContacts';
import { ContactType } from '@/core/domain/_shared/ContactType';
import { cellphoneMask } from '@/utils/mask/commonMasks';
import WhatsappTemplatesSelectField from '@/components/_common/WhatsappTemplatesSelectField';
import { useWorkOrderChecklistImageList } from '@/core/domain/workorder/WorkOrdersChecklist/WorkOrdersChecklist.hooks';
import { useReportTokenGenerate } from '@/core/domain/reports/reportToken/reportToken.hooks';
import { ReportTokenType } from '@/core/domain/reports/reportToken/reportToken.types';
import { useWindowSize } from '@/hooks';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import { useMatrix } from '@/core/domain/enterprise/Matrix/Matrix.hooks';
import { MatrixPartnerOrigin } from '@/types/Register';
import { landlineMask } from '@/utils/mask/commonMasks/cellphoneMask';

interface Props extends ModalProps {
  onCancel: VoidFunction;
  phoneNumber?: string;
  customerId?: number;
}

const ComplementWhatsappModal: React.FC<Props> = ({
  phoneNumber,
  customerId,
  ...props
}) => {
  const [form] = Form.useForm<{
    number: string;
    wppText: string;
    hideHourCost: boolean;
  }>();

  const { isMobile } = useWindowSize();

  const { data: matrix } = useMatrix();

  const wppText = Form.useWatch('wppText', form);
  const wppNumber = Form.useWatch('number', form);
  const foreignNumber = Form.useWatch('foreignNumber', form);

  const { data: contactsData } = useCustomerContacts(customerId, props.open);

  const contacts = flatContacts(contactsData).filter((contact) => {
    return (
      contact.contactType === ContactType.Phone ||
      contact.contactType === ContactType.Mobile ||
      contact.contactType === ContactType.WhatsApp ||
      (contact.contactType === ContactType.Foreign &&
        /^\d+$/.test(contact.value.replace(/\s+/g, '')) &&
        foreignNumber)
    );
  });

  const contactOptions = contacts.map((contact) => {
    const phone = removeMask(contact.value);
    const withMask =
      phone.length === 11 && contact.contactType !== ContactType.Foreign;

    return {
      value: phone,
      label: withMask ? applyMask(phone, cellphoneMask) : phone,
      contentType: contact.contactType,
    };
  });

  const filterTagsForBosch = useCallback(
    (tag: string) => {
      const isBoschOrigin =
        !!matrix?.partnerOrigin &&
        [
          MatrixPartnerOrigin.BOSCH_CAR_SERVICE,
          MatrixPartnerOrigin.BOSCH_MODULE_SPECIALIST,
        ].includes(matrix?.partnerOrigin);

      if (!isBoschOrigin && tag === '{{boschCoReURL}}') return false;

      return true;
    },
    [matrix?.partnerOrigin],
  );

  const tags = Object.keys(wppTags)
    .filter(filterTagsForBosch)
    .map((tag) => ({
      value: tag,
      label: wppTags[tag],
    }));

  const {
    workorder: { data: workorder },
    complement: { data: complement },
  } = useWorkorderDetailContext();

  const { data: checklistImages } = useWorkOrderChecklistImageList(
    {
      workOrderComplementId: complement?.id,
    },
    props.open,
  );

  const { data: workorderItems } = useWorkOrderItems(
    props.open && !!complement?.id ? complement.id : undefined,
  );

  useEffect(() => {
    form.setFieldsValue({
      number: phoneNumber ? phoneNumber : undefined,
      wppText: wppDefaultWppText,
    });
  }, [phoneNumber, props.open]);

  async function onSubmit() {
    try {
      const values = await form.validateFields();

      const phoneNumber = values.number;

      const contactOption = contactOptions.find(
        (option) => option.value === phoneNumber,
      );

      sendMessage(
        removeMask(phoneNumber),
        values.hideHourCost,
        contactOption?.contentType === ContactType.Foreign,
      );
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const formError = error as any;

      if (formError?.values && !formError?.values?.number) {
        return message.error('Informe o número do cliente');
      } else {
        return notificationError(error);
      }
    }
  }

  const reportTokenGenerate = useReportTokenGenerate();

  async function sendMessage(
    number: string,
    hideHourCost: boolean,
    isForeign = false,
  ) {
    try {
      if (workorder && complement) {
        let arToken: { token: string } | undefined;
        let checklistToken: { token: string } | undefined;

        if (wppText?.includes('{{urlDoOrcamento}}')) {
          arToken = await reportTokenGenerate.mutateAsync({
            params: JSON.stringify({
              workOrderId: workorder.id,
              complementNumber: complement.number,
              includeCancelledParts: false,
              hideHourCost,
            }),
            reportType: ReportTokenType.ADMINISTRATIVE,
          });
        }

        if (wppText?.includes('{{urlDoChecklist}}')) {
          checklistToken = await reportTokenGenerate.mutateAsync({
            params: JSON.stringify({
              WorkOrderComplementId: complement.id,
            }),
            reportType: ReportTokenType.CHECKLIST,
          });
        }

        const textToSend = wppTextFormat({
          wppText,
          workorder,
          complement,
          workorderItems,
          checklistImages: checklistImages?.listChecklistImage.map(
            (image) => image.filePath,
          ),
          arToken: arToken?.token,
          checklistToken: checklistToken?.token,
        });

        const phoneNumber =
          number.length === 11 && !isForeign ? `55${number}` : number;

        const urlQueryString = new URLSearchParams({
          phone: phoneNumber,
          text: textToSend,
        }).toString();

        if (isMobile) {
          window.open(
            `https://wa.me/${phoneNumber}?text=${encodeURIComponent(
              textToSend,
            )}`,
            '_blank',
          );
        } else {
          window.open(
            `https://web.whatsapp.com/send?${urlQueryString}`,
            '_blank',
          );
        }
      }
    } catch {}
  }

  return (
    <Modal
      {...props}
      title="Orçamento por WhatsApp"
      footer={
        <Space>
          <Button onClick={props.onCancel}>Cancelar</Button>
          <Tooltip
            trigger={['hover', 'click']}
            title={
              (!wppNumber || removeMask(wppNumber).length < 11) &&
              'Insira um número para o envio'
            }
          >
            <Button
              icon={<FaExternalLinkAlt />}
              disabled={!wppNumber || removeMask(wppNumber).length < 10}
              onClick={onSubmit}
            >
              Enviar
            </Button>
          </Tooltip>
        </Space>
      }
      destroyOnClose
    >
      <Form form={form} preserve={false}>
        <Space direction="vertical">
          <Typography.Text>
            Para enviar o link do relatório do orçamento, preencha o número do
            WhatsApp do cliente.
          </Typography.Text>

          <Form.Item
            name="foreignNumber"
            valuePropName="checked"
            initialValue={false}
            noStyle
          >
            <Checkbox>Número extrangeiro</Checkbox>
          </Form.Item>

          <FloatingFormItem
            name="number"
            label="Número do cliente"
            getValueFromEvent={(value, option) => {
              const cleanValue = removeMask(value);
              const length = cleanValue.length;

              if (
                length > 11 ||
                option.contentType === ContactType.Foreign ||
                foreignNumber
              ) {
                return cleanValue;
              }

              if (length === 10) {
                return applyMask(cleanValue, landlineMask);
              }

              if (length === 11) {
                return applyMask(cleanValue, cellphoneMask);
              }

              return cleanValue;
            }}
            extra={
              foreignNumber
                ? 'Para preenchimento manual, informar Cód. país e DDD'
                : undefined
            }
          >
            <AutoComplete
              getPopupContainer={(triggerNode) => triggerNode.parentNode}
              options={contactOptions}
            />
          </FloatingFormItem>

          <FloatingFormItem
            name="whatsappTemplateId"
            label="Template do Whatsapp"
            extra={
              <Typography.Link
                style={{ fontWeight: 'bold' }}
                onClick={() =>
                  window.open('/app/settings/whatsappTemplates', '_blank')
                }
              >
                Gerenciar Template
              </Typography.Link>
            }
          >
            <WhatsappTemplatesSelectField
              getPopupContainer={(triggerNode) => triggerNode.parentNode}
              onTemplateSelect={(obj) => {
                if (obj) {
                  form.setFieldValue('wppText', obj.message);
                }
              }}
            />
          </FloatingFormItem>

          <Group position="between">
            <Typography.Text>Template da mensagem:</Typography.Text>

            <Popover
              content={
                <div style={{ width: 400 }}>
                  {tags.map((tag) => (
                    <div key={tag.value} style={{ marginBottom: 16 }}>
                      <Typography.Text>{`${tag.value}: ${tag.label}`}</Typography.Text>
                    </div>
                  ))}
                </div>
              }
              placement="bottomRight"
            >
              <Button type="link">Tags</Button>
            </Popover>
          </Group>

          <Typography.Text>
            Colocar textos entre (*) deixará o texto em negrito no WhatsApp.
          </Typography.Text>

          <Form.Item name="wppText" noStyle initialValue={wppDefaultWppText}>
            <TextareaVars tags={tags} rows={10} />
          </Form.Item>

          {hasPermissions([
            Authorities.MOVIMENTATION_WORKORDER_VIEW_COST_HOUR_ADMINISTRATIVE_BUDGET_REPORTS,
          ]) &&
            wppText?.includes('{{urlDoOrcamento}}') && (
              <FloatingFormItem
                valuePropName="checked"
                name="hideHourCost"
                initialValue={false}
              >
                <Checkbox>
                  <Tooltip title="Ao selecionar essa opção, os campos 'Valor Unitário' e 'Valor Líquido' serão ocultados no relatório.">
                    Ocultar detalhes dos valores no relatório
                  </Tooltip>
                </Checkbox>
              </FloatingFormItem>
            )}

          <Alert
            type="info"
            description={
              <div
                style={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                  wordWrap: 'break-word',
                }}
                dangerouslySetInnerHTML={{
                  __html: `${wppTextFormat({
                    wppText,
                    workorder,
                    complement,
                    workorderItems,
                    checklistImages: checklistImages?.listChecklistImage.map(
                      (image) => image.filePath,
                    ),
                  })}`,
                }}
              />
            }
          />
        </Space>
      </Form>
    </Modal>
  );
};

export default ComplementWhatsappModal;
